import LoadingSpinner from '@/components/animations/LoadingSpinner/LoadingSpinner';
import Icon from '@/components/icons/Icon';
import { _t } from '@/locale';
import { useState } from 'react';
import ServiceCategoryList from '../components/ServiceCategoryList';
import { MATCHED_SERVICES_CATEGORY_ID, matchServicesByQuery } from './SelectService.helpers';
import { Button } from '@/components/elements/forms/buttons';
import { SelectServiceCategory, SelectServiceProps } from './SelectService.types';

const baseKey = 'features.bookingSelection.SelectService';

const CenteredMessage = ({ children }: { children: string }) => (
  <p className="py-xl text-m text-fg_secondary text-center">{children}</p>
);

const LoadingState = () => (
  <div className="py-3xl gap-lg flex w-full flex-col items-center justify-center" role="status" aria-live="polite">
    <LoadingSpinner loop style={{ width: 24, height: 24 }} />
    <p className="text-m text-fg_primary text-center">{_t(`${baseKey}.loadingLabel`)}</p>
  </div>
);

/**
 * Presentational select-service view; props come from useSelectService (derived
 * from BookState). Owns only local UI state, no Redux.
 */
const SelectService = ({
  isNarrow,
  categories,
  onPickService,
  onShowServiceInfo,
  onHideServiceInfo,
  onOpenServiceInfo,
  ctaAction,
  employeePanel,
  footer,
  hideHeader = false,
  isLoading = false,
  hasError = false,
}: SelectServiceProps) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Owned here (not per accordion row) so a category's expand state survives being
  // filtered out of — and back into — the list while searching.
  const [expandedById, setExpandedById] = useState<Record<number, boolean>>({});
  const toggleCategory = (categoryId: number, next: boolean) =>
    setExpandedById((prev) => ({ ...prev, [categoryId]: next }));

  const isSearching = isSearchOpen && query.trim().length > 0;
  const matchedCategory: SelectServiceCategory = {
    id: MATCHED_SERVICES_CATEGORY_ID,
    name: _t(`${baseKey}.search.matchedServices`),
    services: isSearching ? matchServicesByQuery(categories, query) : [],
    defaultExpanded: true,
  };
  const visibleCategories = isSearching ? (matchedCategory.services.length ? [matchedCategory] : []) : categories;
  const clearSearch = () => {
    setQuery('');
    setIsSearchOpen(false);
  };

  const body = (() => {
    if (hasError) return <CenteredMessage>{_t(`${baseKey}.errorLabel`)}</CenteredMessage>;
    if (isLoading) return <LoadingState />;
    if (visibleCategories.length === 0) {
      const emptyKey = isSearching ? 'noSearchResultsLabel' : 'emptyLabel';
      return <CenteredMessage>{_t(`${baseKey}.${emptyKey}`)}</CenteredMessage>;
    }
    return (
      <ServiceCategoryList
        categories={visibleCategories}
        isNarrow={isNarrow}
        forceExpandAll={isSearching}
        expandedById={expandedById}
        onToggleCategory={toggleCategory}
        onPickService={onPickService}
        onShowServiceInfo={onShowServiceInfo}
        onHideServiceInfo={onHideServiceInfo}
        onOpenServiceInfo={onOpenServiceInfo}
        ctaAction={ctaAction}
      />
    );
  })();

  const showAllCta = isSearching && visibleCategories.length > 0 && (
    <Button label={_t(`${baseKey}.search.showAllCta`)} variant="link" size="md" onClick={clearSearch} />
  );

  const serviceColumn = (
    <section
      aria-label={_t('components.modules.modals.BookAppointment.views.PickServiceView.title')}
      className="gap-sm flex w-full flex-col">
      {!hideHeader && (
        <div className="gap-sm flex items-center justify-between">
          <h2 className="text-fg_primary text-xl font-bold">{_t(`${baseKey}.categoriesHeading`)}</h2>
          <button
            type="button"
            aria-label={_t(`${baseKey}.search.ariaLabel`)}
            aria-expanded={isSearchOpen}
            onClick={() => setIsSearchOpen((prev) => !prev)}
            className="bg-bg_secondary_bold p-xs flex shrink-0 items-center justify-center rounded-full">
            <Icon variant="search" size="sm" color="fg_primary" />
          </button>
        </div>
      )}
      {!hideHeader && isSearchOpen && (
        // The field is collapsed to an icon by default and revealed on toggle to keep the header compact.
        <input
          type="text"
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={_t(`${baseKey}.search.placeholder`)}
          aria-label={_t(`${baseKey}.search.ariaLabel`)}
          className="border-border_regular px-md py-sm text-l text-fg_primary w-full rounded-md border"
        />
      )}
      {body}
      {showAllCta}
      {!hasError && !isLoading && footer}
    </section>
  );

  if (isNarrow) {
    return (
      <div className="gap-xl flex w-full flex-col">
        {employeePanel}
        {serviceColumn}
      </div>
    );
  }

  return (
    <div className="gap-4xl flex w-full items-start">
      <div className="min-w-0 flex-1">{serviceColumn}</div>
      {employeePanel && <div className="w-80 shrink-0">{employeePanel}</div>}
    </div>
  );
};

export default SelectService;
